import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { projectAPI, taskAPI, externalReviewAPI, projectLinkAPI, departmentAPI } from '../../api/services';
import { Project, Task, ProjectComment, ExternalReview, User, ProjectLink } from '../../types';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { clearFolders, fetchFoldersByProject, Folder } from '../../store/folderSlice';
import { clearSprintError } from '../../store/sprintSlice';

// Sub-components (overview)
import ProjectHeader from './ProjectDetail/ProjectHeader';
import ProjectTitle from './ProjectDetail/ProjectTitle';
import ProjectProperties from './ProjectDetail/ProjectProperties';
import ProjectDescription from './ProjectDetail/ProjectDescription';
import ProjectTeam from './ProjectDetail/ProjectTeam';
import ProjectTasks from './ProjectDetail/ProjectTasks';
import ProjectWorkTree from './ProjectDetail/ProjectWorkTree';
import ProjectExternalReviews from './ProjectDetail/ProjectExternalReviews';
import ProjectActivitySidebar from './ProjectDetail/ProjectActivitySidebar';
import ReviewDetailsModal from './ProjectDetail/ReviewDetailsModal';
import ProjectKnowledgeBase from './ProjectDetail/ProjectKnowledgeBase';

// Sprint
import SprintList from './Sprints/SprintList';
import SprintDetail from './Sprints/SprintDetail';
import SprintForm from './Sprints/SprintForm';
import BacklogView from './Sprints/BacklogView';

// Bugs
import BugInbox from './Bugs/BugInbox';
import BugDetail from './Bugs/BugDetail';
import BugReportForm from './Bugs/BugReportForm';

// Folders
import FolderTree from './Folders/FolderTree';
import FolderDetail from './Folders/FolderDetail';
import CreateFolderModal from './Folders/CreateFolderModal';

// Features
import FeatureList from './Features/FeatureList';
import FeatureDetail from './Features/FeatureDetail';
import FeatureForm from './Features/FeatureForm';
import FeatureApprovalQueue from './Features/FeatureApprovalQueue';

// Subprojects (disabled — using subfolders instead)
// import SubprojectList from './ProjectDetail/SubprojectList';

// Story Points
import Leaderboard from './StoryPoints/Leaderboard';

// ─── Types ────────────────────────────────────────────────────────────────────

type View =
  | 'overview'
  | 'sprints'
  | 'sprint-detail'
  | 'sprint-new'
  | 'backlog'
  | 'bugs'
  | 'bug-detail'
  | 'bug-new'
  | 'features'
  | 'feature-detail'
  | 'feature-new'
  | 'links'
  // | 'subprojects'
  | 'approvals'
  | 'leaderboard'
  | 'kb'
  | 'team'
  | 'folder'
  | 'activity';

// ─── Nav item ─────────────────────────────────────────────────────────────────

const NavIcon = {
  overview: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
    </svg>
  ),
  sprints: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5" />
    </svg>
  ),
  backlog: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zM3.75 12h.007v.008H3.75V12zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm-.375 5.25h.007v.008H3.75v-.008zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
    </svg>
  ),
  bugs: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 12.75c1.148 0 2.278.08 3.383.237 1.037.146 1.866.966 1.866 2.013 0 3.728-2.35 6.75-5.25 6.75S6.75 18.728 6.75 15c0-1.046.83-1.867 1.866-2.013A24.204 24.204 0 0112 12.75zm0 0c2.883 0 5.647.508 8.207 1.44a23.91 23.91 0 01-1.152 6.06M12 12.75c-2.883 0-5.647.508-8.208 1.44a23.916 23.916 0 001.152 6.06M12 12.75a2.25 2.25 0 002.248-2.354M12 12.75a2.25 2.25 0 01-2.248-2.354M12 8.25c.995 0 1.971-.08 2.922-.236.403-.066.74-.358.795-.762a3.778 3.778 0 00-.399-2.25M12 8.25c-.995 0-1.97-.08-2.922-.236-.402-.066-.74-.358-.795-.762a3.734 3.734 0 01.4-2.253M12 8.25a2.25 2.25 0 00-2.248 2.146M12 8.25a2.25 2.25 0 012.248 2.146M8.683 5a6.032 6.032 0 01-1.155-1.002c.07-.63.27-1.222.574-1.747m.581 2.749A3.75 3.75 0 0115.318 5m0 0c.427-.283.815-.62 1.155-.999a4.471 4.471 0 00-.575-1.752M4.921 6a24.048 24.048 0 00-.392 3.314c1.668.546 3.416.914 5.223 1.082M19.08 6c.205 1.08.337 2.187.392 3.314a23.882 23.882 0 01-5.223 1.082" />
    </svg>
  ),
  folder: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" />
    </svg>
  ),
  kb: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
    </svg>
  ),
  team: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
    </svg>
  ),
  features: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M14.25 6.087c0-.355.186-.676.401-.959.221-.29.349-.634.349-1.003 0-1.036-1.007-1.875-2.25-1.875s-2.25.84-2.25 1.875c0 .369.128.713.349 1.003.215.283.401.604.401.959v0a.64.64 0 01-.657.643 48.491 48.491 0 01-4.163-.3c.186 1.613.293 3.25.315 4.907a.656.656 0 01-.658.663v0c-.355 0-.676-.186-.959-.401a1.647 1.647 0 00-1.003-.349c-1.036 0-1.875 1.007-1.875 2.25s.84 2.25 1.875 2.25c.369 0 .713-.128 1.003-.349.283-.215.604-.401.959-.401v0c.31 0 .555.26.532.57a48.039 48.039 0 01-.642 5.056c1.518.19 3.058.309 4.616.354a.64.64 0 00.657-.643v0c0-.355-.186-.676-.401-.959a1.647 1.647 0 01-.349-1.003c0-1.035 1.008-1.875 2.25-1.875 1.243 0 2.25.84 2.25 1.875 0 .369-.128.713-.349 1.003-.215.283-.4.604-.4.959v0c0 .333.277.599.61.58a48.1 48.1 0 005.427-.63 48.05 48.05 0 00.582-4.717.532.532 0 00-.533-.57v0c-.355 0-.676.186-.959.401-.29.221-.634.349-1.003.349-1.035 0-1.875-1.007-1.875-2.25s.84-2.25 1.875-2.25c.37 0 .713.128 1.003.349.283.215.604.401.96.401v0a.656.656 0 00.658-.663 48.422 48.422 0 00-.37-5.36c-1.886.342-3.81.574-5.766.689a.578.578 0 01-.61-.58v0z" />
    </svg>
  ),
  links: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
    </svg>
  ),
  activity: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  subprojects: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 018.25 20.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
    </svg>
  ),
  approvals: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
    </svg>
  ),
  leaderboard: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 18.75h-9m9 0a3 3 0 013 3h-15a3 3 0 013-3m9 0v-3.375c0-.621-.503-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 01-.982-3.172M9.497 14.25a7.454 7.454 0 00.981-3.172M5.25 4.236c-.982.143-1.954.317-2.916.52A6.003 6.003 0 007.73 9.728M5.25 4.236V4.5c0 2.108.966 3.99 2.48 5.228M5.25 4.236V2.721C7.456 2.41 9.71 2.25 12 2.25c2.291 0 4.545.16 6.75.47v1.516M18.75 4.236c.982.143 1.954.317 2.916.52A6.003 6.003 0 0016.27 9.728M18.75 4.236V4.5c0 2.108-.966 3.99-2.48 5.228m0 0a6.023 6.023 0 01-3.52 1.122 6.023 6.023 0 01-3.52-1.122" />
    </svg>
  ),
};

const NAV_ITEMS: { view: View; label: string }[] = [
  { view: 'overview', label: 'Overview' },
  // { view: 'subprojects', label: 'Subprojects' },
  { view: 'sprints', label: 'Sprints' },
  { view: 'backlog', label: 'Backlog' },
  { view: 'bugs', label: 'Bugs' },
  { view: 'features', label: 'Features' },
  { view: 'approvals', label: 'Approvals' },
  { view: 'folder', label: 'Folders' },
  { view: 'links', label: 'Links' },
  { view: 'kb', label: 'Knowledge Base' },
  { view: 'team', label: 'Team' },
  { view: 'activity', label: 'Activity' },
  { view: 'leaderboard', label: 'Leaderboard' },
];

// ─── Main component ───────────────────────────────────────────────────────────

const VALID_VIEWS: View[] = [
  'overview', 'sprints', 'sprint-detail', 'sprint-new', 'backlog',
  'bugs', 'bug-detail', 'bug-new', 'features', 'feature-detail', 'feature-new',
  'links', 'approvals', 'leaderboard', 'kb', 'team', 'folder', 'activity',
];

const ProjectDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(state => state.auth);
  const { folders: allFolders } = useAppSelector(state => state.folders);

  // Core project state
  const [project, setProject] = useState<Project | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [comments, setComments] = useState<ProjectComment[]>([]);
  const [externalReviews, setExternalReviews] = useState<ExternalReview[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [approvalDetails, setApprovalDetails] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Workspace navigation — synced with URL ?view= param
  const viewParam = searchParams.get('view') as View | null;
  const activeView: View = viewParam && VALID_VIEWS.includes(viewParam) ? viewParam : 'overview';

  const setActiveView = useCallback((view: View) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (view === 'overview') {
        next.delete('view');
      } else {
        next.set('view', view);
      }
      return next;
    }, { replace: false }); // replace: false pushes to history so back button works
  }, [setSearchParams]);
  const selectedSprintId = searchParams.get('sprintId');
  const selectedBugId = searchParams.get('bugId');
  const selectedFolderId = searchParams.get('folderId');
  const selectedFeatureId = searchParams.get('featureId');

  const setSelectedSprintId = useCallback((val: string | null) => {
    setSearchParams(prev => { const n = new URLSearchParams(prev); val ? n.set('sprintId', val) : n.delete('sprintId'); return n; }, { replace: true });
  }, [setSearchParams]);
  const setSelectedBugId = useCallback((val: string | null) => {
    setSearchParams(prev => { const n = new URLSearchParams(prev); val ? n.set('bugId', val) : n.delete('bugId'); return n; }, { replace: true });
  }, [setSearchParams]);
  const setSelectedFolderId = useCallback((val: string | null) => {
    setSearchParams(prev => { const n = new URLSearchParams(prev); val ? n.set('folderId', val) : n.delete('folderId'); return n; }, { replace: true });
  }, [setSearchParams]);
  const setSelectedFeatureId = useCallback((val: string | null) => {
    setSearchParams(prev => { const n = new URLSearchParams(prev); val ? n.set('featureId', val) : n.delete('featureId'); return n; }, { replace: true });
  }, [setSearchParams]);
  const [projectLinks, setProjectLinks] = useState<ProjectLink[]>([]);
  const [linkedProjectDetails, setLinkedProjectDetails] = useState<Record<string, any>>({});
  const [showFolderTree, setShowFolderTree] = useState(true);
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkDepartments, setLinkDepartments] = useState<any[]>([]);
  const [linkForm, setLinkForm] = useState({ targetDepartmentId: '', linkType: 'related', description: '' });
  const [linkCreating, setLinkCreating] = useState(false);
  const [pauseReason, setPauseReason] = useState('');
  const [resumeAdjust, setResumeAdjust] = useState(false);
  const [resumeNotes, setResumeNotes] = useState('');

  // Modals
  const [selectedReview, setSelectedReview] = useState<ExternalReview | null>(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (id) {
      fetchProject();
      fetchTasks();
      fetchComments();
      fetchExternalReviews();
      fetchUsers();
      fetchApprovalDetails();
    }
    return () => {
      dispatch(clearFolders());
      dispatch(clearSprintError());
    };
  }, [id]);

  // Refetch data when navigating back to overview (so changes made in sub-views are visible)
  const [overviewKey, setOverviewKey] = useState(0);
  useEffect(() => {
    if (activeView === 'overview' && id) {
      fetchTasks();
      setOverviewKey(k => k + 1);
    }
  }, [activeView]);

  const fetchProject = async () => {
    try {
      setLoading(true);
      const res = await projectAPI.getProjectById(id!);
      setProject(res.data.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load project');
    } finally {
      setLoading(false);
    }
  };

  const fetchTasks = async () => {
    try {
      const res = await projectAPI.getTasks(id!);
      setTasks(res.data.data || []);
    } catch { /* non-fatal */ }
  };

  const fetchComments = async () => {
    try {
      const res = await projectAPI.getComments(id!);
      setComments(res.data.data || []);
    } catch { /* non-fatal */ }
  };

  const fetchExternalReviews = async () => {
    try {
      const res = await externalReviewAPI.getProjectReviews(id!);
      setExternalReviews(res.data.data || []);
    } catch { /* non-fatal */ }
  };

  const fetchUsers = async () => {
    try {
      const res = await projectAPI.getProjectUsers();
      setUsers(res.data || []);
    } catch { /* non-fatal */ }
  };

  const fetchApprovalDetails = async () => {
    if (!id) return;
    try {
      const res = await projectAPI.getProjectApprovalDetails(id);
      setApprovalDetails(res.data?.data || null);
    } catch { /* non-fatal */ }
  };

  const updateProjectField = useCallback(async (field: string, value: any) => {
    if (!project) return;
    const updateData: any = {};
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      updateData[parent] = { ...(project as any)[parent], [child]: value };
    } else {
      updateData[field] = value;
    }
    try {
      await projectAPI.updateProject(project._id, updateData);
      await fetchProject();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to update project');
    }
  }, [project]);

  const handleCreateTask = useCallback(async (taskData: any) => {
    const availableUsers = [
      { _id: project!.owner.userId, name: project!.owner.name, email: project!.owner.email },
      ...project!.members.map((m: any) => ({ _id: m.userId, name: m.name, email: '' })),
    ];
    const assignedTo = (taskData.assignedUsers || []).map((uid: string) => {
      const u = availableUsers.find(a => a._id === uid);
      return { userId: uid, name: u?.name || '', email: u?.email || '', assignedAt: new Date().toISOString() };
    });
    await taskAPI.createTask({
      title: taskData.title,
      description: taskData.description || taskData.title,
      projectId: id,
      priority: taskData.priority,
      dueDate: taskData.dueDate || undefined,
      status: 'todo',
      type: 'feature',
      assignedTo: assignedTo.length > 0 ? assignedTo : undefined,
    });
    fetchTasks();
    fetchProject();
  }, [project, id]);

  // ─── Navigation helpers ────────────────────────────────────────────────────

  const navTo = useCallback((view: View) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (view === 'overview') {
        next.delete('view');
      } else {
        next.set('view', view);
      }
      // Clear sub-selections when switching top-level views
      if (view !== 'sprint-detail' && view !== 'sprint-new') next.delete('sprintId');
      if (view !== 'bug-detail') next.delete('bugId');
      if (view !== 'folder') next.delete('folderId');
      if (view !== 'feature-detail') next.delete('featureId');
      return next;
    });
  }, [setSearchParams]);

  const handleSelectSprint = useCallback((sprintId: string) => {
    setSearchParams(prev => {
      const n = new URLSearchParams(prev);
      n.set('view', 'sprint-detail');
      n.set('sprintId', sprintId);
      return n;
    });
  }, [setSearchParams]);

  const handleSelectBug = useCallback((bugId: string) => {
    setSearchParams(prev => {
      const n = new URLSearchParams(prev);
      n.set('view', 'bug-detail');
      n.set('bugId', bugId);
      return n;
    });
  }, [setSearchParams]);

  const handleSelectFolder = useCallback((folderId: string | null) => {
    setSearchParams(prev => {
      const n = new URLSearchParams(prev);
      n.set('view', 'folder');
      folderId ? n.set('folderId', folderId) : n.delete('folderId');
      return n;
    });
  }, [setSearchParams]);

  const handleSelectFeature = useCallback((featureId: string) => {
    setSearchParams(prev => {
      const n = new URLSearchParams(prev);
      n.set('view', 'feature-detail');
      n.set('featureId', featureId);
      return n;
    });
  }, [setSearchParams]);

  const fetchLinks = useCallback(async () => {
    if (!id) return;
    try {
      const res = await projectLinkAPI.getByProject(id);
      const links = res.data.data ?? [];
      setProjectLinks(links);
      // Fetch details for approved linked projects
      const details: Record<string, any> = {};
      for (const link of links) {
        if (link.status === 'approved') {
          const linkedId = link.sourceProjectId === id ? link.targetProjectId : link.sourceProjectId;
          if (linkedId && !details[linkedId]) {
            try {
              const pRes = await projectAPI.getProjectById(linkedId);
              details[linkedId] = pRes.data.data ?? pRes.data;
            } catch { /* access denied or not found — skip */ }
          }
        }
      }
      setLinkedProjectDetails(details);
    } catch { /* ignore */ }
  }, [id]);

  useEffect(() => {
    if (activeView === 'links' && id) fetchLinks();
    if (activeView === 'folder' && id) dispatch(fetchFoldersByProject(id));
  }, [activeView, id, fetchLinks]);

  useEffect(() => {
    if (showLinkModal) departmentAPI.getAllDepartments({ scope: 'all' }).then(r => setLinkDepartments(r.data.data ?? r.data ?? [])).catch(() => {});
  }, [showLinkModal]);

  const handleCreateLink = async () => {
    if (!id || !linkForm.targetDepartmentId || linkCreating) return;
    setLinkCreating(true);
    try {
      await projectLinkAPI.create({ sourceProjectId: id, targetDepartmentId: linkForm.targetDepartmentId, linkType: linkForm.linkType, description: linkForm.description || undefined });
      setShowLinkModal(false);
      setLinkForm({ targetDepartmentId: '', linkType: 'related', description: '' });
      fetchLinks();
    } catch { /* ignore */ }
    setLinkCreating(false);
  };

  const handlePause = async () => {
    if (!id) return;
    try {
      await projectAPI.pause(id, { reason: pauseReason });
      setPauseReason('');
      setShowPauseModal(false);
      fetchProject();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to pause');
    }
  };

  const handleResume = async () => {
    if (!id) return;
    try {
      await projectAPI.resume(id, { deadlinesAdjusted: resumeAdjust, adjustmentNotes: resumeNotes });
      setResumeAdjust(false);
      setResumeNotes('');
      setShowResumeModal(false);
      fetchProject();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to resume');
    }
  };

  // ─── Status helpers ────────────────────────────────────────────────────────

  const STATUS_SEQUENCE = ['planning', 'development', 'testing', 'sit', 'uat', 'done', 'production', 'archived'] as const;

  const advanceStatus = useCallback(async () => {
    if (!project || !canEdit) return;
    const currentIdx = STATUS_SEQUENCE.indexOf(project.status as any);
    if (currentIdx === -1 || currentIdx >= STATUS_SEQUENCE.length - 1) return;
    const nextStatus = STATUS_SEQUENCE[currentIdx + 1];
    await updateProjectField('status', nextStatus);
  }, [project, canEdit, updateProjectField]);

  const getStatusColor = (status: string) => {
    const c: Record<string, string> = {
      planning: 'bg-gray-100 text-gray-700', development: 'bg-blue-100 text-blue-700',
      testing: 'bg-yellow-100 text-yellow-700', sit: 'bg-orange-100 text-orange-700',
      uat: 'bg-purple-100 text-purple-700', done: 'bg-green-100 text-green-700',
      production: 'bg-emerald-100 text-emerald-700', archived: 'bg-gray-200 text-gray-600',
    };
    return c[status] || 'bg-gray-100 text-gray-700';
  };

  const getTaskStatusColor = (status: string) => {
    const c: Record<string, string> = {
      todo: 'bg-gray-100 text-gray-700', in_progress: 'bg-blue-100 text-blue-700',
      blocked: 'bg-red-100 text-red-700', review: 'bg-yellow-100 text-yellow-700',
      done: 'bg-green-100 text-green-700',
    };
    return c[status] || 'bg-gray-100 text-gray-700';
  };

  const getReviewStatusColor = (status: string) => {
    const c: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-700', completed: 'bg-green-100 text-green-700',
      expired: 'bg-red-100 text-red-700',
    };
    return c[status] || 'bg-gray-100 text-gray-700';
  };

  // Permissions
  const isOwner = project && user && project.owner.userId === (user.id || user._id);
  const isMember = project && user && project.members.some((m: any) => m.userId === (user.id || user._id));
  const canEdit = !!(isOwner || isMember || user?.role === 'admin');

  // ─── Loading / error ────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="min-h-screen bg-white py-8 px-8">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error || 'Project not found'}
        </div>
      </div>
    );
  }

  // ─── Render ─────────────────────────────────────────────────────────────────

  // Compute which top-level nav item is active (for highlighting)
  const activePrimary: View =
    activeView === 'sprint-detail' || activeView === 'sprint-new' ? 'sprints'
    : activeView === 'bug-detail' ? 'bugs'
    : activeView;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Project header bar */}
      <ProjectHeader
        project={project}
        canEdit={!!(isOwner || user?.role === 'admin')}
        onDelete={() => setShowDeleteConfirm(true)}
      />

      {/* Approval banners */}
      <ApprovalBanners project={project} approvalDetails={approvalDetails} />

      {/* Workspace body: sidebar + content */}
      <div className="flex min-h-[calc(100vh-120px)]">

        {/* ── Left sidebar ─────────────────────────────────────────────── */}
        <aside className="w-56 flex-shrink-0 bg-white border-r border-gray-200 flex flex-col">
          {/* Project name */}
          <div className="px-4 pt-5 pb-3 border-b border-gray-100">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Project</p>
            <p className="text-sm font-semibold text-gray-900 truncate" title={project.name}>{project.name}</p>
            {canEdit && STATUS_SEQUENCE.indexOf(project.status as any) < STATUS_SEQUENCE.length - 1 ? (
              <button
                onClick={advanceStatus}
                title={`Click to advance to ${STATUS_SEQUENCE[STATUS_SEQUENCE.indexOf(project.status as any) + 1]}`}
                className={`mt-1 inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium cursor-pointer hover:ring-2 hover:ring-offset-1 hover:ring-blue-400 transition-all ${getStatusColor(project.status)}`}
              >
                {project.status}
                <svg className="w-3 h-3 opacity-60" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            ) : (
              <span className={`mt-1 inline-flex text-xs px-2 py-0.5 rounded-full font-medium ${getStatusColor(project.status)}`}>
                {project.status}
              </span>
            )}
          </div>

          {/* Nav */}
          <nav className="flex-1 py-2 overflow-y-auto">
            {NAV_ITEMS.map(({ view, label }) => (
              <button
                key={view}
                onClick={() => navTo(view)}
                className={`w-full flex items-center gap-2.5 px-4 py-2 text-sm transition-colors text-left ${
                  activePrimary === view
                    ? 'bg-blue-50 text-blue-700 font-medium border-r-2 border-blue-600'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <span className="flex-shrink-0">{NavIcon[view as keyof typeof NavIcon]}</span>
                {label}
              </button>
            ))}

            {/* Folder tree section */}
            {(activeView === 'folder' || showFolderTree) && (
              <div className="mt-2 border-t border-gray-100">
                <button
                  onClick={() => setShowFolderTree(v => !v)}
                  className="w-full flex items-center justify-between px-4 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider hover:text-gray-600"
                >
                  <span>Folder Tree</span>
                  <svg
                    className={`w-3 h-3 transition-transform ${showFolderTree ? 'rotate-180' : ''}`}
                    fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {showFolderTree && (
                  <FolderTree
                    projectId={id!}
                    selectedFolderId={selectedFolderId}
                    onSelectFolder={handleSelectFolder}
                  />
                )}
              </div>
            )}
          </nav>
        </aside>

        {/* ── Main content ─────────────────────────────────────────────── */}
        <main className="flex-1 min-w-0 flex flex-col">
          <div className="flex-1">

            {/* ── PAUSE BANNER ──────────────────────────────────────── */}
            {project.isPaused && (
              <div className="mx-6 mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center justify-between">
                <div>
                  <span className="text-sm font-semibold text-yellow-800">Project Paused</span>
                  <p className="text-xs text-yellow-600 mt-0.5">All timelines are frozen. Resume to continue work.</p>
                </div>
                {canEdit && (
                  <button onClick={() => setShowResumeModal(true)} className="bg-yellow-600 text-white px-3 py-1.5 rounded text-sm font-medium hover:bg-yellow-700">
                    Resume Project
                  </button>
                )}
              </div>
            )}

            {/* ── OVERVIEW ──────────────────────────────────────────── */}
            {activeView === 'overview' && (
              <div className="px-8 py-6 space-y-6">
                {/* Pause button in overview when not paused */}
                {canEdit && !project.isPaused && (
                  <div className="flex justify-end">
                    <button onClick={() => setShowPauseModal(true)} className="text-xs text-gray-500 border border-gray-200 px-3 py-1.5 rounded hover:bg-gray-50">
                      Pause Project
                    </button>
                  </div>
                )}
                <ProjectTitle project={project} canEdit={canEdit} onBlur={async e => {
                  const v = e.target.value.trim();
                  if (v && v !== project.name) await updateProjectField('name', v);
                }} onStatusChange={canEdit ? (status) => updateProjectField('status', status) : undefined} />
                <ProjectProperties project={project} canEdit={canEdit} onUpdate={updateProjectField} getStatusColor={getStatusColor} />
                <ProjectDescription project={project} canEdit={canEdit} onBlur={async e => {
                  const v = e.target.value.trim();
                  if (v !== project.description) await updateProjectField('description', v);
                }} />
                <ProjectTeam
                  project={project} canEdit={canEdit} allUsers={users}
                  onAddMember={async uid => { await projectAPI.addMember(id!, uid); fetchProject(); }}
                  onRemoveMember={async mid => { await projectAPI.removeMember(id!, mid); fetchProject(); }}
                />
                <ProjectWorkTree
                  key={overviewKey}
                  projectId={id!}
                  tasks={tasks}
                  onSelectFolder={fid => handleSelectFolder(fid)}
                  onSelectFeature={fid => handleSelectFeature(fid)}
                />
                <ProjectExternalReviews
                  externalReviews={externalReviews} canEdit={canEdit}
                  onRequestReview={async data => {
                    await externalReviewAPI.requestReview(id!, data);
                    fetchExternalReviews();
                  }}
                  onViewDetails={r => { setSelectedReview(r); setShowReviewModal(true); }}
                  getReviewStatusColor={getReviewStatusColor}
                />
                {id && <ProjectKnowledgeBase projectId={id} />}
              </div>
            )}

            {/* ── SPRINTS LIST ──────────────────────────────────────── */}
            {activeView === 'sprints' && (
              <div className="p-6 w-full">
                <SprintList
                  projectId={id!}
                  onSelectSprint={handleSelectSprint}
                  onNewSprint={() => setActiveView('sprint-new')}
                />
              </div>
            )}

            {/* ── SPRINT DETAIL ─────────────────────────────────────── */}
            {activeView === 'sprint-detail' && selectedSprintId && (
              <div className="p-6 w-full">
                <SprintDetail
                  sprintIdProp={selectedSprintId}
                  projectId={id}
                  onBack={() => setSearchParams(prev => { const n = new URLSearchParams(prev); n.set('view', 'sprints'); n.delete('sprintId'); return n; })}
                />
              </div>
            )}

            {/* ── NEW SPRINT ────────────────────────────────────────── */}
            {activeView === 'sprint-new' && (
              <div className="p-6 max-w-xl w-full">
                <button
                  onClick={() => setActiveView('sprints')}
                  className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 mb-4"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                  </svg>
                  Back to sprints
                </button>
                <SprintForm
                  projectId={id!}
                  onCreated={sprintId => setSearchParams(prev => { const n = new URLSearchParams(prev); n.set('view', 'sprint-detail'); n.set('sprintId', sprintId); return n; })}
                />
              </div>
            )}

            {/* ── BACKLOG ───────────────────────────────────────────── */}
            {activeView === 'backlog' && (
              <div className="p-6 w-full">
                <BacklogView projectId={id!} />
              </div>
            )}

            {/* ── BUG INBOX ─────────────────────────────────────────── */}
            {activeView === 'bugs' && (
              <div className="p-6 w-full">
                <BugInbox
                  projectId={id}
                  onSelectBug={handleSelectBug}
                  onReportBug={() => setActiveView('bug-new')}
                />
              </div>
            )}

            {/* ── BUG DETAIL ────────────────────────────────────────── */}
            {activeView === 'bug-detail' && selectedBugId && (
              <div className="p-6 w-full">
                <BugDetail
                  bugIdProp={selectedBugId}
                  onBack={() => setSearchParams(prev => { const n = new URLSearchParams(prev); n.set('view', 'bugs'); n.delete('bugId'); return n; })}
                />
              </div>
            )}

            {/* ── BUG REPORT FORM ───────────────────────────────────── */}
            {activeView === 'bug-new' && (
              <div className="p-6 w-full">
                <BugReportForm
                  projectId={id}
                  onCreated={bugId => setSearchParams(prev => { const n = new URLSearchParams(prev); n.set('view', 'bug-detail'); n.set('bugId', bugId); return n; })}
                  onCancel={() => setActiveView('bugs')}
                />
              </div>
            )}

            {/* ── FEATURES LIST ──────────────────────────────────────── */}
            {activeView === 'features' && (
              <div className="p-6 w-full">
                <FeatureList
                  projectId={id!}
                  onSelectFeature={handleSelectFeature}
                  onNewFeature={() => setActiveView('feature-new')}
                />
              </div>
            )}

            {/* ── FEATURE DETAIL ──────────────────────────────────────── */}
            {activeView === 'feature-detail' && selectedFeatureId && (
              <div className="p-6 w-full">
                <FeatureDetail
                  featureIdProp={selectedFeatureId}
                  projectId={id}
                  onBack={() => setSearchParams(prev => { const n = new URLSearchParams(prev); n.set('view', 'features'); n.delete('featureId'); return n; })}
                />
              </div>
            )}

            {/* ── FEATURE FORM ─────────────────────────────────────────── */}
            {activeView === 'feature-new' && (
              <div className="p-6 max-w-2xl w-full">
                <button
                  onClick={() => setActiveView('features')}
                  className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 mb-4"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                  </svg>
                  Back to features
                </button>
                <FeatureForm
                  projectId={id || ''}
                  onCreated={fId => setSearchParams(prev => { const n = new URLSearchParams(prev); n.set('view', 'feature-detail'); n.set('featureId', fId); return n; })}
                  onCancel={() => setActiveView('features')}
                />
              </div>
            )}

            {/* ── LINKS ────────────────────────────────────────────────── */}
            {activeView === 'links' && (
              <div className="p-6 w-full">
                {projectLinks.length === 0 && !showLinkModal ? (
                  <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-8">
                    <div className="w-20 h-20 rounded-2xl bg-blue-50 flex items-center justify-center mb-6">
                      <svg className="w-10 h-10 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
                      </svg>
                    </div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">No project links</h2>
                    <p className="text-sm text-gray-500 max-w-sm mb-8">Link this project to other department projects to track cross-team dependencies and progress.</p>
                    <button
                      onClick={() => { setShowLinkModal(true); fetchLinks(); }}
                      className="bg-blue-600 text-white px-6 py-3 rounded-xl text-sm font-medium hover:bg-blue-700 shadow-sm transition-colors"
                    >
                      Link a Department
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h2 className="text-lg font-semibold text-gray-900">Linked Projects</h2>
                      <button
                        onClick={() => setShowLinkModal(true)}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700"
                      >
                        + Link Department
                      </button>
                    </div>
                    <div className="space-y-2">
                      {projectLinks.map(link => {
                        const isSource = link.sourceProjectId === id;
                        const linkedProjectId = isSource ? link.targetProjectId : link.sourceProjectId;
                        const linkedName = isSource ? (link.targetProjectName || link.targetDepartmentName || 'Unknown') : link.sourceProjectName;
                        const statusColor: Record<string, string> = {
                          pending: 'bg-yellow-100 text-yellow-700',
                          approved: 'bg-green-100 text-green-700',
                          rejected: 'bg-red-100 text-red-700',
                          cancelled: 'bg-gray-100 text-gray-500',
                        };

                        // Approved link with a target project — summary card with progress
                        if (link.status === 'approved' && linkedProjectId) {
                          const detail = linkedProjectDetails[linkedProjectId];
                          const stats = detail?.stats ?? {};
                          const totalTasks = stats.totalTasks ?? 0;
                          const completedTasks = stats.completedTasks ?? 0;
                          const pct = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
                          const statusColor: Record<string, string> = { planning: 'bg-gray-100 text-gray-700', development: 'bg-blue-100 text-blue-700', testing: 'bg-purple-100 text-purple-700', done: 'bg-green-100 text-green-700', production: 'bg-emerald-100 text-emerald-700' };

                          return (
                            <div
                              key={link._id}
                              onClick={() => navigate(`/employee/projects/${linkedProjectId}`)}
                              className="bg-white border border-gray-200 rounded-xl p-4 cursor-pointer hover:border-blue-300 hover:shadow-sm transition-all"
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap mb-1">
                                    <span className="text-sm font-semibold text-gray-900">{linkedName}</span>
                                    {detail?.status && <span className={`text-xs px-2 py-0.5 rounded-full ${statusColor[detail.status] || 'bg-gray-100 text-gray-700'}`}>{detail.status}</span>}
                                    <span className="text-xs text-gray-400 capitalize">{link.linkType.replace('_', ' ')}</span>
                                  </div>
                                  {link.description && <p className="text-xs text-gray-500 mb-2">{link.description}</p>}
                                  {/* Progress bar */}
                                  {totalTasks > 0 && (
                                    <div className="mb-2">
                                      <div className="flex justify-between text-xs text-gray-500 mb-1">
                                        <span>{completedTasks}/{totalTasks} tasks</span>
                                        <span>{pct}%</span>
                                      </div>
                                      <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                        <div className={`h-full rounded-full transition-all ${pct === 100 ? 'bg-green-500' : 'bg-blue-500'}`} style={{ width: `${pct}%` }} />
                                      </div>
                                    </div>
                                  )}
                                  <div className="flex gap-3 text-xs text-gray-400">
                                    {detail?.members && <span>{detail.members.length} members</span>}
                                    {detail?.departmentName && <span>{detail.departmentName}</span>}
                                    {detail?.stats?.lastActivityAt && <span>Active {new Date(detail.stats.lastActivityAt).toLocaleDateString()}</span>}
                                  </div>
                                </div>
                                <svg className="w-4 h-4 text-gray-400 mt-1 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                                </svg>
                              </div>
                            </div>
                          );
                        }

                        // Approved link without target project — awaiting selection
                        if (link.status === 'approved' && !linkedProjectId) {
                          return (
                            <div key={link._id} className="bg-white border border-gray-200 rounded-lg px-4 py-3 flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">awaiting project selection</span>
                                <span className="text-xs text-gray-400 capitalize">{link.linkType.replace('_', ' ')}</span>
                                <span className="text-sm font-medium text-gray-900">{linkedName}</span>
                                {link.description && <span className="text-xs text-gray-500">-- {link.description}</span>}
                              </div>
                            </div>
                          );
                        }

                        // Pending / rejected / cancelled
                        return (
                          <div key={link._id} className="bg-white border border-gray-200 rounded-lg px-4 py-3 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusColor[link.status]}`}>{link.status}</span>
                              <span className="text-xs text-gray-400 capitalize">{link.linkType.replace('_', ' ')}</span>
                              <span className="text-sm font-medium text-gray-900">{linkedName}</span>
                              {link.description && <span className="text-xs text-gray-500">-- {link.description}</span>}
                            </div>
                            <div className="flex gap-2">
                              {link.status === 'pending' && !isSource && (
                                <>
                                  <button onClick={async () => { await projectLinkAPI.approve(link._id); fetchLinks(); }} className="text-xs bg-green-50 text-green-700 border border-green-200 px-2 py-1 rounded hover:bg-green-100">Approve</button>
                                  <button onClick={async () => { await projectLinkAPI.reject(link._id); fetchLinks(); }} className="text-xs bg-red-50 text-red-700 border border-red-200 px-2 py-1 rounded hover:bg-red-100">Reject</button>
                                </>
                              )}
                              {link.status === 'pending' && isSource && (
                                <button onClick={async () => { await projectLinkAPI.cancel(link._id); fetchLinks(); }} className="text-xs text-gray-500 border border-gray-200 px-2 py-1 rounded hover:bg-gray-50">Cancel</button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Create Link Modal */}
                {showLinkModal && (
                  <div className="!mt-0 fixed inset-0 bg-black/40 flex items-center justify-center" style={{ zIndex: 9999 }}>
                    <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Link to Another Department</h3>

                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                          <select
                            value={linkForm.targetDepartmentId}
                            onChange={e => setLinkForm(f => ({ ...f, targetDepartmentId: e.target.value }))}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="">Select a department...</option>
                            {linkDepartments.map((d: any) => (
                              <option key={d._id} value={d._id}>{d.name}</option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Link Type</label>
                          <select
                            value={linkForm.linkType}
                            onChange={e => setLinkForm(f => ({ ...f, linkType: e.target.value }))}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="related">Related</option>
                            <option value="depends_on">Depends On</option>
                            <option value="blocks">Blocks</option>
                            <option value="child_of">Child Of</option>
                            <option value="shared_resource">Shared Resource</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
                          <textarea
                            value={linkForm.description}
                            onChange={e => setLinkForm(f => ({ ...f, description: e.target.value }))}
                            placeholder="Describe the relationship..."
                            rows={3}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                          />
                        </div>
                      </div>

                      <div className="flex justify-end gap-3 mt-6">
                        <button
                          onClick={() => { setShowLinkModal(false); setLinkForm({ targetDepartmentId: '', linkType: 'related', description: '' }); }}
                          className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleCreateLink}
                          disabled={!linkForm.targetDepartmentId || linkCreating}
                          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {linkCreating ? 'Creating...' : 'Create Link'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── FOLDERS ───────────────────────────────────────────── */}
            {activeView === 'folder' && (
              <div className="p-6 w-full">
                {selectedFolderId ? (
                  <FolderDetail
                    folderId={selectedFolderId}
                    projectId={id!}
                    onNavigateToFolder={fid => setSelectedFolderId(fid)}
                    onSelectFeature={fid => handleSelectFeature(fid)}
                  />
                ) : (() => {
                  const rootFolders = allFolders.filter((f: Folder) => f.depth === 0);
                  return rootFolders.length === 0 ? (
                    <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-8">
                      <div className="w-20 h-20 rounded-2xl bg-blue-50 flex items-center justify-center mb-6">
                        <svg className="w-9 h-9 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" />
                        </svg>
                      </div>
                      <h2 className="text-xl font-semibold text-gray-900 mb-2">No folders yet</h2>
                      <p className="text-sm text-gray-500 max-w-sm mb-8">
                        Folders help you organize tasks, features, and files into logical groups.
                      </p>
                      <button
                        onClick={() => setShowCreateFolderModal(true)}
                        className="bg-blue-600 text-white px-6 py-3 rounded-xl text-sm font-medium hover:bg-blue-700 shadow-sm transition-colors"
                      >
                        Create a Folder
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h2 className="text-lg font-semibold text-gray-900">Folders</h2>
                        <button
                          onClick={() => setShowCreateFolderModal(true)}
                          className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700"
                        >
                          + New Folder
                        </button>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {rootFolders.map((folder: Folder) => {
                          const childCount = allFolders.filter((f: Folder) => f.hierarchyPath.includes(folder._id) && f._id !== folder._id).length;
                          return (
                            <div
                              key={folder._id}
                              onClick={() => setSelectedFolderId(folder._id)}
                              className="bg-white border border-gray-200 rounded-xl p-4 cursor-pointer hover:border-blue-300 hover:shadow-sm transition-all group"
                            >
                              <div className="flex items-start gap-3">
                                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center shrink-0 group-hover:bg-blue-100 transition-colors">
                                  <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" />
                                  </svg>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h3 className="text-sm font-semibold text-gray-900 truncate">{folder.name}</h3>
                                  {folder.description && (
                                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{folder.description}</p>
                                  )}
                                  <div className="flex gap-3 mt-2 text-xs text-gray-400">
                                    {childCount > 0 && <span>{childCount} subfolder{childCount !== 1 ? 's' : ''}</span>}
                                    {folder.estimatedEndDate && <span>Due {new Date(folder.estimatedEndDate).toLocaleDateString()}</span>}
                                    <span className={`px-1.5 py-0.5 rounded text-xs ${
                                      folder.visibility === 'private' ? 'bg-gray-100 text-gray-600' :
                                      folder.visibility === 'public_org' ? 'bg-blue-50 text-blue-600' :
                                      'bg-green-50 text-green-600'
                                    }`}>{folder.visibility === 'public_org' ? 'Org' : folder.visibility}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* ── KNOWLEDGE BASE ────────────────────────────────────── */}
            {activeView === 'kb' && (
              <div className="p-6 w-full">
                {id && <ProjectKnowledgeBase projectId={id} />}
              </div>
            )}

            {/* ── TEAM ──────────────────────────────────────────────── */}
            {activeView === 'team' && (
              <div className="p-6 w-full">
                <ProjectTeam
                  project={project} canEdit={canEdit} allUsers={users}
                  onAddMember={async uid => { await projectAPI.addMember(id!, uid); fetchProject(); }}
                  onRemoveMember={async mid => { await projectAPI.removeMember(id!, mid); fetchProject(); }}
                />
              </div>
            )}

            {/* ── SUBPROJECTS (disabled — using subfolders instead) ── */}
            {/* {activeView === 'subprojects' && (
              <div className="p-6 w-full">
                <SubprojectList projectId={id!} />
              </div>
            )} */}

            {/* ── APPROVALS ───────────────────────────────────────── */}
            {activeView === 'approvals' && (
              <div className="p-6 w-full">
                <FeatureApprovalQueue projectId={id} />
              </div>
            )}

            {/* ── LEADERBOARD ─────────────────────────────────────── */}
            {activeView === 'leaderboard' && (
              <div className="p-6 w-full">
                <Leaderboard projectId={id!} />
              </div>
            )}

            {/* ── ACTIVITY ──────────────────────────────────────────── */}
            {activeView === 'activity' && (
              <div className="p-6 w-full max-w-2xl">
                <ProjectActivitySidebar
                  project={project}
                  comments={comments}
                  onAddComment={async content => {
                    await projectAPI.addComment(id!, { content });
                    fetchComments();
                    fetchProject();
                  }}
                />
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Modals */}
      <ReviewDetailsModal
        review={selectedReview}
        isOpen={showReviewModal}
        onClose={() => { setShowReviewModal(false); setSelectedReview(null); }}
      />

      {showDeleteConfirm && (
        <div className="!mt-0 fixed inset-0 bg-black/50 flex items-center justify-center" style={{ zIndex: 9999 }}>
          <div className="bg-white rounded-xl shadow-xl p-8 max-w-md w-full mx-4 text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Delete Project</h3>
            <p className="text-sm text-gray-500 mb-6">
              Are you sure you want to delete "{project.name}"? This cannot be undone.
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  await projectAPI.deleteProject(id!);
                  navigate(-1);
                }}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Folder Modal */}
      {showCreateFolderModal && id && (
        <CreateFolderModal
          projectId={id}
          onClose={() => setShowCreateFolderModal(false)}
          onCreated={() => {
            setShowCreateFolderModal(false);
            dispatch(fetchFoldersByProject(id));
          }}
        />
      )}

      {/* Pause Modal */}
      {showPauseModal && (
        <div className="!mt-0 fixed inset-0 bg-black/40 flex items-center justify-center" style={{ zIndex: 9999 }}>
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm mx-4">
            <h3 className="font-semibold text-gray-900 mb-3">Pause Project</h3>
            <p className="text-sm text-gray-500 mb-3">All active timelines will freeze. Members will be notified.</p>
            <textarea
              value={pauseReason}
              onChange={e => setPauseReason(e.target.value)}
              placeholder="Reason for pausing (optional)"
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-yellow-500 mb-4"
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowPauseModal(false)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
              <button onClick={handlePause} className="px-4 py-2 bg-yellow-600 text-white rounded-lg text-sm font-medium hover:bg-yellow-700">Pause</button>
            </div>
          </div>
        </div>
      )}

      {/* Resume Modal */}
      {showResumeModal && (
        <div className="!mt-0 fixed inset-0 bg-black/40 flex items-center justify-center" style={{ zIndex: 9999 }}>
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm mx-4">
            <h3 className="font-semibold text-gray-900 mb-3">Resume Project</h3>
            <label className="flex items-center gap-2 text-sm text-gray-700 mb-3">
              <input type="checkbox" checked={resumeAdjust} onChange={e => setResumeAdjust(e.target.checked)} className="rounded" />
              Extend all deadlines by the pause duration
            </label>
            <textarea
              value={resumeNotes}
              onChange={e => setResumeNotes(e.target.value)}
              placeholder="Notes (optional)"
              rows={2}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-green-500 mb-4"
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowResumeModal(false)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
              <button onClick={handleResume} className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700">Resume</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Approval banners (extracted to keep main component readable) ─────────────

const ApprovalBanners: React.FC<{ project: Project; approvalDetails: any }> = ({ project, approvalDetails }) => {
  if (!project) return null;
  return (
    <>
      {project.approvalStatus === 'pending' && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mx-8 mt-4">
          <p className="text-sm text-yellow-800 font-medium">Project Pending Approval</p>
          <p className="text-xs text-yellow-700 mt-1">
            {approvalDetails?.approvers?.length > 0
              ? `Awaiting approval from: ${approvalDetails.approvers.slice(0, 4).map((a: any) => a.name).join(', ')}${approvalDetails.approvers.length > 4 ? ', and others' : ''}.`
              : 'Awaiting approval from department leadership.'}
          </p>
        </div>
      )}
      {project.approvalStatus === 'rejected' && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mx-8 mt-4">
          <p className="text-sm text-red-800 font-medium">Project Not Approved</p>
          <p className="text-xs text-red-700 mt-1">This project was rejected. Please review feedback and resubmit.</p>
        </div>
      )}
      {project.approvalStatus === 'approved' && (
        <div className="bg-green-50 border-l-4 border-green-400 p-4 mx-8 mt-4">
          <p className="text-sm text-green-800 font-medium">Project Approved</p>
          <p className="text-xs text-green-700 mt-1">This project has been approved and is ready for implementation.</p>
        </div>
      )}
    </>
  );
};

export default ProjectDetail;
